import { memo, useContext, useEffect } from 'react';
import { useCurrentEditor, EditorEvents } from '@tiptap/react';
import {
  BatchUpdateCommentLocation,
  useWorkerCommentsStore,
} from '../../../hooks/useWorkerCommentsStore';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import { shallow } from 'zustand/shallow';
import {
  CommentTransactions,
  CommentEvents,
  getSelectionFromEditor,
} from '../../../components/editor/plugins/comment';

interface WorkerCommentBridgeProps {
  enabled: boolean;
  childId: string;
}

function WorkerCommentBridge({ childId, enabled }: WorkerCommentBridgeProps) {
  const taskContext = useContext(ChatTaskContext);
  const { editor } = useCurrentEditor();

  const setSelectedComment = useWorkerCommentsStore(state => state.selectWorkerComment);
  const selectedComment = useWorkerCommentsStore(state => state.selectedWorkerComment);
  const setSelection = useWorkerCommentsStore(state => state.setSelection);

  // NOTE: Handling enable for Comment extension
  useEffect(() => {
    if (!editor) return;
    if (enabled) {
      editor.commands.enableComments();
    } else {
      editor.commands.disableComments();
    }
  }, [editor, enabled]);

  // NOTE: Handling comment updates for Comment extension
  useEffect(() => {
    if (!editor || !enabled) return;
    const unsubscribeFromCommentUpdates = useWorkerCommentsStore.subscribe(
      state =>
        state.workerComments.filter(
          comment =>
            comment.childId === childId &&
            comment.locationStart !== undefined &&
            comment.locationEnd !== undefined,
        ),
      newComments => {
        editor.commands.setComments(
          newComments
            .filter(comment => !comment.feedbackResponse)
            .map(comment => ({
              id: comment.workerCommentId,
              range: {
                from: comment.locationStart!,
                to: comment.locationEnd!,
              },
            })),
        );
      },
      { fireImmediately: true, equalityFn: shallow },
    );
    return () => {
      unsubscribeFromCommentUpdates();
    };
  }, [editor, enabled, childId]);

  useEffect(() => {
    if (!editor || !enabled) return;

    function handleTransactions({ transaction }: EditorEvents['transaction']) {
      const batchedUpdates: CommentEvents['batchCommentRangeUpdated'] | undefined =
        transaction.getMeta(CommentTransactions.CommentPositionsUpdated);
      const commentSelected: CommentEvents['commentSelected'] | undefined = transaction.getMeta(
        CommentTransactions.CommentSelected,
      );
      if (commentSelected) {
        setSelectedComment(commentSelected);
      }
      if (batchedUpdates) {
        const updates: Array<BatchUpdateCommentLocation> = batchedUpdates.map(comment => ({
          id: comment.id,
          updater: () => {
            return [comment.range.from, comment.range.to];
          },
        }));

        useWorkerCommentsStore.getState().batchUpdateWorkerCommentLocations(updates);
      }
    }

    editor.on('transaction', handleTransactions);

    return () => {
      editor.off('transaction', handleTransactions);
    };
  }, [editor, enabled, setSelectedComment]);

  useEffect(() => {
    if (!editor || !enabled) return;
    if (!selectedComment) {
      editor.commands.deselectComment();
      return;
    } else {
      editor.commands.selectComment(selectedComment);
    }
  }, [selectedComment, editor, enabled]);

  useEffect(() => {
    /**
     * Enabled only for Unified Feedback, since Unified Feedback relies on the `selection` property of WorkerCommentStore
     */
    if (!editor || !taskContext.enableUnifiedFeedback) {
      return;
    }

    function handleSelectionChange({ editor }: EditorEvents['selectionUpdate']) {
      const { range, content } = getSelectionFromEditor(editor);
      if (range) {
        setSelection({
          from: range.from,
          to: range.to,
          empty: false,
          childId,
          text: content,
        });
      } else {
        setSelection({
          from: 0,
          to: 0,
          empty: true,
          childId: '',
        });
      }
    }
    editor.on('selectionUpdate', handleSelectionChange);
    return () => {
      editor.off('selectionUpdate', handleSelectionChange);
    };
  }, [editor, childId, setSelection, taskContext.enableUnifiedFeedback]);

  return null;
}

export default memo(WorkerCommentBridge);
